
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const useIdleTimeout = () => {
  const authContext = useAuth();
  
  // Safety check - if context is not available, return early
  if (!authContext) {
    console.warn('useIdleTimeout: AuthContext not available');
    return { resetTimeout: () => {} };
  }
  
  const { signOut, user } = authContext;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (user) {
      timeoutRef.current = setTimeout(() => {
        console.log('User idle timeout - logging out');
        signOut();
      }, IDLE_TIMEOUT);
    }

    lastActivityRef.current = Date.now();
  }, [user, signOut]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    // Only reset if more than 1 minute has passed since last activity
    if (now - lastActivityRef.current > 60000) {
      resetTimeout();
    }
  }, [resetTimeout]);

  useEffect(() => {
    if (!user) return;

    // Set up event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timeout
    resetTimeout();

    return () => {
      // Clean up event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, handleActivity, resetTimeout]);

  return { resetTimeout };
};
